import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import { FileTextOutlined, BarChartOutlined, HistoryOutlined, SettingOutlined, CrownOutlined } from '@ant-design/icons';
import Login from './components/Login';
import Register from './components/Register';
import RecordPage from './components/RecordPage';
import RecordHistory from './components/RecordHistory';
import StatsPage from './components/StatsPage';
import SettingsPage from './components/SettingsPage';
import AdminPage from './components/AdminPage';
import ImportPage from './components/ImportPage';
import api, { removeToken, getUser } from './services/auth';

const { Header, Content } = Layout;

function useWindowSize() {
  const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
  useEffect(() => {
    const onResize = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

function AppContent({ user, onLogout, onUserUpdate }) {
  const [width] = useWindowSize();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = width < 768;
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get('/admin/check').then(res => {
      setIsAdmin(res.data.isAdmin);
    }).catch(() => {
      setIsAdmin(false);
    });
  }, []);

  const getSelectedKey = () => {
    if (location.pathname === '/history') return '2';
    if (location.pathname === '/stats') return '3';
    if (location.pathname === '/settings') return '4';
    if (location.pathname === '/admin') return '5';
    return '1';
  };

  const menuOnClick = (key) => {
    if (key === '1') navigate('/');
    else if (key === '2') navigate('/history');
    else if (key === '3') navigate('/stats');
    else if (key === '4') navigate('/settings');
    else if (key === '5') navigate('/admin');
  };

  if (isMobile) {
    return (
      <Layout style={{ minHeight: '100vh', paddingBottom: 64, background: '#f5f5f7' }}>
        <Header className="apple-header" style={{
          padding: '0 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          position: 'sticky',
          top: 0,
          zIndex: 99,
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1d1d1f', letterSpacing: -0.5 }}>快速记录</div>
          <div style={{ fontSize: 14, color: '#86868b', fontWeight: 500 }}>{user.username}</div>
        </Header>
        <Content style={{ margin: '12px', overflow: 'auto', flex: 1 }}>
          <Routes>
            <Route path="/" element={<RecordPage isMobile={isMobile} />} />
            <Route path="/history" element={<RecordHistory isMobile={isMobile} />} />
            <Route path="/stats" element={<StatsPage isMobile={isMobile} />} />
            <Route path="/settings" element={<SettingsPage isMobile={isMobile} onLogout={onLogout} user={user} onUserUpdate={onUserUpdate} />} />
            <Route path="/admin" element={isAdmin ? <AdminPage isMobile={isMobile} /> : <Navigate to="/" replace />} />
            <Route path="/import/:code" element={<ImportPage isMobile={isMobile} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
        <div className="apple-bottom-bar" style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}>
          <Menu
            mode="horizontal"
            selectedKeys={[getSelectedKey()]}
            onClick={({ key }) => menuOnClick(key)}
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              margin: 0,
              border: 'none',
              background: 'transparent',
              height: 56,
              lineHeight: '56px',
            }}
            items={[
              { key: '1', icon: <FileTextOutlined style={{ fontSize: 18 }} />, label: <span style={{ fontSize: 11, fontWeight: 500 }}>记录</span> },
              { key: '2', icon: <HistoryOutlined style={{ fontSize: 18 }} />, label: <span style={{ fontSize: 11, fontWeight: 500 }}>历史</span> },
              { key: '3', icon: <BarChartOutlined style={{ fontSize: 18 }} />, label: <span style={{ fontSize: 11, fontWeight: 500 }}>统计</span> },
              { key: '4', icon: <SettingOutlined style={{ fontSize: 18 }} />, label: <span style={{ fontSize: 11, fontWeight: 500 }}>设置</span> },
              ...(isAdmin ? [{ key: '5', icon: <CrownOutlined style={{ fontSize: 18 }} />, label: <span style={{ fontSize: 11, fontWeight: 500 }}>管理</span> }] : []),
            ]}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f7' }}>
      <Layout.Sider
        className="apple-sider"
        width={220}
        breakpoint="lg"
        collapsedWidth={80}
        style={{
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 700,
          color: '#1d1d1f',
          letterSpacing: -0.5,
        }}>
          快速记录
        </div>
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          onClick={({ key }) => menuOnClick(key)}
          style={{
            border: 'none',
            background: 'transparent',
            fontWeight: 500,
          }}
          items={[
            { key: '1', icon: <FileTextOutlined />, label: '记录页面' },
            { key: '2', icon: <HistoryOutlined />, label: '记录历史' },
            { key: '3', icon: <BarChartOutlined />, label: '统计页面' },
            { key: '4', icon: <SettingOutlined />, label: '设置' },
            ...(isAdmin ? [{ type: 'divider' }, { key: '5', icon: <CrownOutlined style={{ color: '#ff9500' }} />, label: <span style={{ color: '#ff9500' }}>管理后台</span> }] : []),
          ]}
        />
      </Layout.Sider>
      <Layout style={{ background: '#f5f5f7' }}>
        <Header className="apple-header" style={{
          padding: '0 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          position: 'sticky',
          top: 0,
          zIndex: 99,
        }}>
          <div></div>
          <div style={{ color: '#86868b', fontSize: 14, fontWeight: 500 }}>欢迎，{user.username}</div>
        </Header>
        <Content style={{ margin: '24px', overflow: 'auto' }}>
          <Routes>
            <Route path="/" element={<RecordPage isMobile={isMobile} />} />
            <Route path="/history" element={<RecordHistory isMobile={isMobile} />} />
            <Route path="/stats" element={<StatsPage isMobile={isMobile} />} />
            <Route path="/settings" element={<SettingsPage isMobile={isMobile} onLogout={onLogout} user={user} onUserUpdate={onUserUpdate} />} />
            <Route path="/admin" element={isAdmin ? <AdminPage isMobile={isMobile} /> : <Navigate to="/" replace />} />
            <Route path="/import/:code" element={<ImportPage isMobile={isMobile} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const u = getUser();
    if (u) setUser(u);
  }, []);

  const handleLogout = () => {
    removeToken();
    setUser(null);
  };

  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login onLogin={setUser} />} />
          <Route path="/register" element={<Register onRegister={setUser} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <AppContent user={user} onLogout={handleLogout} onUserUpdate={setUser} />
    </Router>
  );
}

export default App;
